import {
  AgentSupportUserType,
  InvalidAgentSupportUserType,
  UnvalidatedAgentSupportUserType,
  ValidAgentSupportUserType,
} from 'Users/models/User/Agent/AgentSupportUserType';
import * as A from 'Users/models/User/Agent/AgentUserType';
import {
  InvalidVIPUserType,
  UnvalidatedVIPUserType,
  ValidVIPUserType,
  VIPUserType,
} from 'Users/models/User/Agent/VipUserType';
import { IAMUserType, InvalidIAMUserType, UnvalidatedIAMUserType, ValidIAMUserType } from './Agent/IamUserType';

export enum AgentRole {
  Undetermined,
  Agent,
  AgentSupport,
  VIP,
  IAM,
  FieldVicePresident,
  FieldSalesLeader,
}

export type AgentUserTypeValue = ValidAgentUserType['value'] | InvalidAgentUserType['value'];

export class UndeterminedAgentUserType {
  public readonly key = 'UndeterminedAgentUserType';
  public readonly value: never;

  constructor(public readonly agentRole = AgentRole.Undetermined) {}
}

export class InvalidAgentUserType {
  public readonly key = 'InvalidAgentUserType';

  constructor(
    public readonly value:
      | InvalidVIPUserType
      | UndeterminedAgentUserType
      | UnvalidatedVIPUserType
      | A.UnvalidatedAgentUserType
      | A.InvalidAgentUserType
      | UnvalidatedAgentSupportUserType
      | InvalidAgentSupportUserType
      | InvalidIAMUserType
      | UnvalidatedIAMUserType
  ) {}
}

export const isValidVIPUserType = (agentUserTypeValue: AgentUserTypeValue): agentUserTypeValue is ValidAgentUserType['value'] =>
  agentUserTypeValue.key === 'ValidVIPUserType';

const isValidAgentUserType = (
  agentUserTypeValue: AgentUserTypeValue
): agentUserTypeValue is ValidAgentUserType['value'] =>
  agentUserTypeValue.key === 'ValidVIPUserType' ||
  agentUserTypeValue.key === 'ValidAgentUserType' ||
  agentUserTypeValue.key === 'ValidAgentSupportUserType' ||
  agentUserTypeValue.key === 'ValidIAMUserType';

export class ValidAgentUserType {
  public static create(agentUserType: AgentUserTypeValue) {
    return isValidAgentUserType(agentUserType)
      ? new ValidAgentUserType(agentUserType)
      : new InvalidAgentUserType(agentUserType);
  }

  public readonly key = 'ValidAgentUserType';

  constructor(public readonly value: ValidVIPUserType | A.ValidAgentUserType | ValidAgentSupportUserType | ValidIAMUserType) {}
}

export type AgentUserType = ValidAgentUserType | InvalidAgentUserType | UndeterminedAgentUserType;

type AgentUserTypeOptions<TReturn> = {
  Undetermined: () => TReturn;
  VIP: (vipUserType: VIPUserType) => TReturn;
  IAM: (iamUserType: IAMUserType) => TReturn;
  Agent: (agentUserType: A.AgentUserType) => TReturn;
  AgentSupport: (agentSupportUserType: AgentSupportUserType) => TReturn;
};

export const matchAgentUserType = <TReturn>(
  agentUserType: InvalidAgentUserType['value'] | ValidAgentUserType['value'],
  options: AgentUserTypeOptions<TReturn>
): TReturn => {
  switch (agentUserType.key) {
    case 'UndeterminedAgentUserType':
      return options.Undetermined();
    case 'UnvalidatedAgentSupportUserType':
    case 'InvalidAgentSupportUserType':
    case 'ValidAgentSupportUserType':
      return options.AgentSupport(agentUserType);
    case 'UnvalidatedAgentUserType':
    case 'InvalidAgentUserType':
    case 'ValidAgentUserType':
      return options.Agent(agentUserType);
    case 'UnvalidatedVIPUserType':
    case 'InvalidVIPUserType':
    case 'ValidVIPUserType':
      return options.VIP(agentUserType);
    case 'UnvalidatedIAMUserType':
    case 'InvalidIAMUserType':
    case 'ValidIAMUserType':
      return options.IAM(agentUserType);
  }
};

type ValidAgentUserTypeOptions<TReturn> = {
  VIP: (vipUserType: ValidVIPUserType) => TReturn;
  IAM: (iamUserType: ValidIAMUserType) => TReturn;
  Agent: (agentUserType: A.ValidAgentUserType) => TReturn;
  AgentSupport: (agentSupportUserType: ValidAgentSupportUserType) => TReturn;
};

export const matchValidAgentUserType = <TReturn>(
  agentUserType: ValidAgentUserType['value'],
  options: ValidAgentUserTypeOptions<TReturn>
): TReturn => {
  switch (agentUserType.key) {
    case 'ValidAgentSupportUserType':
      return options.AgentSupport(agentUserType);
    case 'ValidAgentUserType':
      return options.Agent(agentUserType);
    case 'ValidVIPUserType':
      return options.VIP(agentUserType);
    case 'ValidIAMUserType':
      return options.IAM(agentUserType);
  }
};
